import { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { apiFetch, ApiError } from '../../services/api-client';
import LiteMemberPanel, { liteErrorCopy, type LiteMember } from './LiteMemberPanel';

// Staff-side pass scanner: reads the QR on a customer's wallet pass
// (bitbrew:v1:{addr}:{shopId}) and stamps their card — the counter path for
// pass-only and cash customers, zero customer phone interaction. Also reads
// "lite" cards (bitbrew:lite:{code} — walletless, shop-created loyalty cards,
// see api/_lib/lite.ts) and routes them to LiteMemberPanel.
// BarcodeDetector where available (Chrome/Android), jsQR canvas fallback
// everywhere else (Safari/iPad has no BarcodeDetector).

interface Props {
  open: boolean;
  onClose: () => void;
  // Preselects the mode on every open — the assisted-signup walkthrough
  // (O4) embeds this scanner already committed to trade-in, so staff never
  // has to find and tap the TRADE-IN toggle mid-customer. Defaults to the
  // scanner's own 'stamp' default when omitted.
  initialMode?: 'stamp' | 'trade-in';
}

type ScanState =
  | { phase: 'scanning' }
  | { phase: 'sending' }
  | { phase: 'done'; member: string; quantity: number; firstVisit?: boolean; birthday?: { title: string; bonus: number } | null; pendingComp?: { code: string } | null }
  | { phase: 'lite'; member: LiteMember }
  | { phase: 'error'; message: string };

export default function ScanPassModal({ open, onClose, initialMode }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [state, setState] = useState<ScanState>({ phase: 'scanning' });
  const [quantity, setQuantity] = useState(1);
  // Full (bitbrew:v1) passes default to the auto-stamp-on-scan flow; toggling
  // to trade-in mode swaps the qty picker for a 1-9 paper-card count and
  // routes the next scanned code through the `trade-in` action instead.
  const [mode, setMode] = useState<'stamp' | 'trade-in'>('stamp');
  const [tradeInStamps, setTradeInStamps] = useState(9);
  const busyRef = useRef(false);

  // The scan loop lives in a long-running effect; it reads the pickers via
  // refs so tapping a qty digit, toggling TRADE-IN, or typing a card count
  // mid-customer doesn't tear down and reacquire the camera stream.
  const quantityRef = useRef(quantity);
  quantityRef.current = quantity;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const tradeInStampsRef = useRef(tradeInStamps);
  tradeInStampsRef.current = tradeInStamps;

  // Fresh session on every open. The dashboard mounts this modal permanently
  // (open just hides it), so component state survives close/reopen — without
  // this reset a sticky TRADE-IN toggle would silently burn the NEXT
  // customer's one-time trade-in instead of stamping them.
  useEffect(() => {
    if (!open) return;
    setState({ phase: 'scanning' });
    setMode(initialMode ?? 'stamp');
    setTradeInStamps(9);
    busyRef.current = false;
  }, [open, initialMode]);

  // Camera runs only while the scanner UI is on screen ('scanning'/'sending').
  // Leaving those phases (lite panel, done, error) stops the stream + rAF
  // loop — so the nested LinkScanner inside LiteMemberPanel never competes
  // for a second camera stream.
  const cameraOn = open && (state.phase === 'scanning' || state.phase === 'sending');

  useEffect(() => {
    if (!cameraOn) return;

    let stream: MediaStream | null = null;
    let raf = 0;
    let cancelled = false;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Detector = (window as any).BarcodeDetector;
    const detector = Detector ? new Detector({ formats: ['qr_code'] }) : null;
    let lastScan = 0;

    async function onCode(raw: string) {
      if (busyRef.current) return;
      if (raw.startsWith('bitbrew:lite:')) {
        busyRef.current = true;
        setState({ phase: 'sending' });
        try {
          const r = await apiFetch<{ member: LiteMember }>('/api/stamp', {
            action: 'lite-lookup', code: raw,
          });
          setState({ phase: 'lite', member: r.member });
        } catch (e) {
          setState({ phase: 'error', message: liteErrorCopy(e, 'LOOKUP FAILED — TRY AGAIN') });
        }
        return;
      }
      if (!raw.startsWith('bitbrew:v1:')) return; // ignore foreign QRs, keep scanning
      busyRef.current = true;
      setState({ phase: 'sending' });
      try {
        if (modeRef.current === 'trade-in') {
          const r = await apiFetch<{ ok: true; stamps: number; member: string }>('/api/stamp', {
            action: 'trade-in', code: raw, stamps: tradeInStampsRef.current,
          });
          setState({ phase: 'done', member: r.member, quantity: r.stamps });
        } else {
          const r = await apiFetch<{ member: string; quantity: number; firstVisit?: boolean; birthday?: { title: string; bonus: number } | null; pendingComp?: { code: string } | null }>('/api/stamp', {
            action: 'scan-stamp', code: raw, quantity: quantityRef.current,
          });
          setState({ phase: 'done', member: r.member, quantity: r.quantity, firstVisit: r.firstVisit, birthday: r.birthday, pendingComp: r.pendingComp });
        }
      } catch (e) {
        // Trade-in errors are code-style (409 already_traded_in etc.) → map to
        // ALL-CAPS copy; scan-stamp errors are already human-readable server
        // sentences (pre-existing contract), keep passing those through.
        const message = modeRef.current === 'trade-in'
          ? liteErrorCopy(e, 'SCAN FAILED — TRY AGAIN')
          : e instanceof ApiError ? e.message : 'Scan failed — try again';
        setState({ phase: 'error', message });
      }
    }

    async function tick(now: number) {
      if (cancelled) return;
      const video = videoRef.current;
      if (video && video.readyState >= 2 && now - lastScan > 120 && !busyRef.current) {
        lastScan = now;
        try {
          if (detector) {
            const codes = await detector.detect(video);
            if (codes[0]?.rawValue) await onCode(codes[0].rawValue);
          } else if (ctx) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            ctx.drawImage(video, 0, 0);
            const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const hit = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
            if (hit?.data) await onCode(hit.data);
          }
        } catch { /* keep scanning */ }
      }
      raf = requestAnimationFrame(tick);
    }

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then((s) => {
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        stream = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current.play().catch(() => {});
        }
        raf = requestAnimationFrame(tick);
      })
      .catch(() => setState({ phase: 'error', message: 'Camera unavailable — allow camera access and retry' }));

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [cameraOn]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(28,25,23,.55)] p-4">
      <div className="w-full max-w-md bg-background rounded-[16px] p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="font-heading text-[9px] text-coral tracking-[1px]">SCAN MEMBER PASS</p>
          <button onClick={onClose} className="font-grotesk font-bold text-[12px] text-body-grey hover:text-primary">✕ CLOSE</button>
        </div>

        {state.phase === 'done' ? (
          <div className="py-10 text-center">
            <p className="font-grotesk font-bold text-2xl text-[#2f9440]">
              +{state.quantity} STAMP{state.quantity > 1 ? 'S' : ''}
            </p>
            <p className="font-grotesk font-bold text-lg mt-3 text-primary">{state.member}</p>
            {state.firstVisit && (
              <p className="font-heading text-[9px] mt-2 text-coral">⭐ FIRST VISIT — STAMPS DOUBLED</p>
            )}
            {state.birthday && (
              <p className="font-heading text-[9px] mt-2 text-gold-ink">
                🎂 BIRTHDAY WEEK — {state.birthday.title.toUpperCase()}
                {state.birthday.bonus > 0 ? ` (+${state.birthday.bonus} AUTO)` : ''}
              </p>
            )}
            {state.pendingComp && (
              <p className="font-heading text-[9px] mt-2 text-coral">
                ☕ HAS A FREE-COFFEE COMP WAITING — CODE {state.pendingComp.code}
              </p>
            )}
            <button
              onClick={() => { busyRef.current = false; setState({ phase: 'scanning' }); }}
              className="rounded-full bg-coral text-white font-grotesk font-bold text-sm py-3 px-6 mt-6"
            >
              SCAN NEXT
            </button>
          </div>
        ) : state.phase === 'lite' ? (
          <LiteMemberPanel
            member={state.member}
            onDone={() => { busyRef.current = false; setState({ phase: 'scanning' }); }}
          />
        ) : state.phase === 'error' ? (
          <div className="py-10 text-center">
            <p className="font-grotesk font-medium text-[13px] text-error">{state.message}</p>
            <button
              onClick={() => { busyRef.current = false; setState({ phase: 'scanning' }); }}
              className="rounded-full bg-white border-2 border-hairline text-body-grey font-grotesk font-bold text-sm py-3 px-6 mt-6"
            >
              TRY AGAIN
            </button>
          </div>
        ) : (
          <>
            {/* Camera preview stays dark — functional, not decorative chrome. */}
            <video ref={videoRef} playsInline muted className="w-full aspect-square object-cover bg-zinc-900 rounded-[10px]" />
            <div className="mt-3 flex items-center justify-between">
              <p className="font-grotesk font-medium text-[12px] text-body-grey">
                {state.phase === 'sending' ? 'STAMPING…' : 'Point at the QR on their wallet pass'}
              </p>
              {mode === 'stamp' ? (
                <div className="flex items-center gap-1">
                  <span className="font-heading text-[8px] text-body-grey">STAMPS</span>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      onClick={() => setQuantity(n)}
                      className={`w-6 h-6 rounded-full font-grotesk font-bold text-[12px] border-2 ${
                        quantity === n ? 'border-coral text-coral' : 'border-hairline text-body-grey'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <span className="font-heading text-[8px] text-body-grey">CARDS</span>
                  <input
                    aria-label="Trade-in stamps"
                    type="number"
                    min={1}
                    max={9}
                    value={tradeInStamps}
                    onChange={(e) => setTradeInStamps(Math.min(9, Math.max(1, Number(e.target.value) || 1)))}
                    className="w-10 bg-white border-2 border-hairline rounded-[8px] font-grotesk font-bold text-[12px] text-primary text-center"
                  />
                </div>
              )}
            </div>
            <div className="mt-2 flex justify-end">
              <button
                onClick={() => setMode(mode === 'trade-in' ? 'stamp' : 'trade-in')}
                className={`rounded-full font-grotesk font-bold text-[11px] px-3 py-1 border-2 ${
                  mode === 'trade-in' ? 'border-gold-ink text-gold-ink' : 'border-hairline text-body-grey'
                }`}
              >
                TRADE-IN
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
